import { cepMask } from '../../helpers/cepMask';
import { dataHoraIsoParaBr, dataIsoParaBr } from '../../helpers/dataBr';
import { nomeDoAprovador } from '../../service/aprovacaoPedido';
import { CirurgiaDoPedido, rotuloTipoCirurgia } from '../../service/cirurgiaDoPedido';
import { IErroIntegracao } from '../../utils/parseErroIntegracao';

/**
 * Folha do pedido para a logística separar o material — principalmente quando
 * o pedido caiu em ERRO_INTEGRACAO e não existe no TOTVS: cirurgia, hospital,
 * paciente e todos os materiais com código, quantidade e valor numa folha só.
 *
 * Feita inteira no front (o PDF do backend não traz o agendamento). A página
 * vai para um iframe com estilo próprio e o navegador imprime ou salva em PDF
 * pela janela de impressão — independe do tema escuro/claro da tela.
 */

export interface DadosImpressao {
  pedido: any;
  cirurgia: CirurgiaDoPedido | null;
  /** O pedido é de URGÊNCIA/ELETIVA mas o agendamento não pôde ser lido. */
  cirurgiaIndisponivel: boolean;
  erroIntegracao: IErroIntegracao | null;
  impressoPor: string;
  agora: Date;
}

const esc = (valor: any): string =>
  String(valor ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string)
  );

const moeda = (valor: any): string =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const campo = (rotulo: string, valor: any): string =>
  `<div class="campo"><span class="rotulo">${esc(rotulo)}</span><span class="valor">${
    esc(valor) || '—'
  }</span></div>`;

function enderecoCompleto(endereco: any): string {
  if (!endereco) return '';
  const rua = [endereco.logradouro, endereco.numero].filter(Boolean).join(', ');
  const cidade = [endereco.cidade, endereco.uf].filter(Boolean).join('/');
  return [rua, endereco.complemento, endereco.bairro, cidade, cepMask(endereco.cep || '')]
    .filter(Boolean)
    .join(' — ');
}

/** Itens de verdade do pedido — o excluído não é para separar. */
export function itensParaSeparar(pedido: any): any[] {
  return (Array.isArray(pedido?.item_pedido) ? pedido.item_pedido : []).filter(
    (item: any) => !item?.excluido
  );
}

export function tituloDaImpressao(pedido: any, cirurgia: CirurgiaDoPedido | null): string {
  const numero = pedido?.pedido_erp || String(pedido?.pedido_id || '').substring(0, 8);
  return [`Pedido ${numero}`, pedido?.clientes?.fantasia, cirurgia?.paciente]
    .filter(Boolean)
    .join(' - ');
}

export function montarHtmlImpressao({
  pedido,
  cirurgia,
  cirurgiaIndisponivel,
  erroIntegracao,
  impressoPor,
  agora,
}: DadosImpressao): string {
  const empresa = pedido?.empresas || {};
  const cliente = pedido?.clientes || {};
  const itens = itensParaSeparar(pedido);
  const totalItens = itens.reduce(
    (acc: number, item: any) => acc + Number(item?.quantidade || 0) * Number(item?.preco_liquido || 0),
    0
  );
  const totalPecas = itens.reduce((acc: number, item: any) => acc + Number(item?.quantidade || 0), 0);
  const frete = Number(pedido?.valor_frete || 0);
  const tipoCirurgia = cirurgia?.tipo || pedido?.tipo_pedido;
  const ehCirurgia = !!cirurgia || cirurgiaIndisponivel;
  const emissao = dataHoraIsoParaBr(pedido?.data_emissao);
  const horaEmissao = String(pedido?.hora_emissao || '').substring(0, 5) || emissao.hora;
  const impressao = dataHoraIsoParaBr(agora.toISOString());
  const feitoPor =
    pedido?.criado_por_nome && pedido?.criado_por_id !== pedido?.colaborador_id
      ? pedido.criado_por_nome
      : '';
  const aprovadoPor = nomeDoAprovador(pedido);

  const avisoErro = erroIntegracao
    ? `<div class="aviso">
        <strong>PEDIDO NÃO INTEGRADO AO TOTVS (erro de integração)</strong>
        <div>Motivo: ${esc(erroIntegracao.motivo)}${
          erroIntegracao.produto ? ` — produto apontado: <strong>${esc(erroIntegracao.produto)}</strong>` : ''
        }</div>
      </div>`
    : '';

  const secaoCirurgia = !ehCirurgia
    ? ''
    : cirurgia
    ? `<section>
        <h2>Cirurgia <span class="selo">${esc(rotuloTipoCirurgia(tipoCirurgia))}</span></h2>
        <div class="grade">
          ${campo('Data da cirurgia', dataIsoParaBr(cirurgia.data))}
          ${campo('Hora', cirurgia.hora)}
          ${campo('Paciente', cirurgia.paciente)}
          ${campo('Médico(a)', cirurgia.medicoCrm ? `${cirurgia.medico} — CRM ${cirurgia.medicoCrm}` : cirurgia.medico)}
          ${campo('Convênio', cirurgia.convenio)}
          ${campo('Tipo de cirurgia', pedido?.tipo_cirurgia)}
        </div>
        ${cirurgia.observacoes ? `<div class="obs"><span class="rotulo">Observações do vendedor</span><p>${esc(cirurgia.observacoes)}</p></div>` : ''}
      </section>`
    : `<section>
        <h2>Cirurgia <span class="selo">${esc(rotuloTipoCirurgia(tipoCirurgia))}</span></h2>
        <p class="falta">Dados do agendamento indisponíveis no momento da impressão.</p>
      </section>`;

  const linhas = itens
    .map((item: any, i: number) => {
      const produto = item?.produtos || {};
      const apontado = !!erroIntegracao?.produto && produto.codigo === erroIntegracao.produto;
      const detalhe = [produto.referencia && `Ref. ${produto.referencia}`, produto.fabricante]
        .filter(Boolean)
        .join(' · ');
      const quantidade = Number(item?.quantidade || 0);
      const unitario = Number(item?.preco_liquido || 0);
      return `<tr${apontado ? ' class="apontado"' : ''}>
        <td class="centro">${i + 1}</td>
        <td>${esc(produto.codigo || item?.produto_id)}</td>
        <td>${esc(produto.nome)}${detalhe ? `<div class="sub">${esc(detalhe)}</div>` : ''}${
          apontado ? '<div class="sub erro">⚠ Produto apontado no erro do TOTVS</div>' : ''
        }</td>
        <td class="centro">${esc(produto.unidade || item?.embalagem || 'UN')}</td>
        <td class="centro forte">${quantidade}</td>
        <td class="direita">${moeda(unitario)}</td>
        <td class="direita">${moeda(quantidade * unitario)}</td>
        <td class="centro caixa"></td>
      </tr>`;
    })
    .join('');

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${esc(tituloDaImpressao(pedido, cirurgia))}</title>
<style>
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 6px; }
  header .empresa { font-size: 14px; font-weight: bold; }
  header .direita { text-align: right; }
  header .numero { font-size: 16px; font-weight: bold; }
  h1 { font-size: 13px; margin: 0 0 2px; letter-spacing: 0.5px; }
  h2 { font-size: 12px; margin: 0 0 6px; padding-bottom: 3px; border-bottom: 1px solid #999; text-transform: uppercase; }
  section { margin-top: 8px; page-break-inside: avoid; }
  .grade { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 12px; }
  .grade.quatro { grid-template-columns: repeat(4, 1fr); }
  .campo { display: flex; flex-direction: column; min-width: 0; }
  .campo.largo { grid-column: 1 / -1; }
  .campo.duplo { grid-column: span 2; }
  .rotulo { font-size: 9px; color: #555; text-transform: uppercase; letter-spacing: 0.3px; }
  .valor { font-size: 12px; font-weight: bold; word-break: break-word; }
  .obs { margin-top: 6px; }
  .obs p { margin: 2px 0 0; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
  .selo { display: inline-block; margin-left: 6px; padding: 1px 6px; border: 1px solid #111; border-radius: 8px; font-size: 10px; }
  .aviso { margin-top: 8px; padding: 6px 8px; border: 2px solid #c53030; background: #fff5f5; color: #9b2c2c; }
  .falta { color: #9b2c2c; margin: 0; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  th { background: #eee; font-size: 9px; text-transform: uppercase; text-align: left; }
  th, td { border: 1px solid #999; padding: 2px 5px; vertical-align: top; }
  tr { page-break-inside: avoid; }
  tr.apontado td { background: #fff5f5; }
  .centro { text-align: center; }
  .direita { text-align: right; white-space: nowrap; }
  .forte { font-weight: bold; font-size: 12px; }
  .caixa { width: 44px; }
  .sub { font-size: 9px; color: #555; margin-top: 1px; }
  .sub.erro { color: #c53030; font-weight: bold; }
  tfoot td { font-weight: bold; }
  .assinaturas { display: flex; gap: 24px; margin-top: 26px; page-break-inside: avoid; }
  .assinaturas div { flex: 1; border-top: 1px solid #111; padding-top: 3px; text-align: center; font-size: 10px; }
  footer { margin-top: 10px; font-size: 9px; color: #555; text-align: right; }
</style>
</head>
<body>
  <header>
    <div>
      <div class="empresa">${esc(empresa.fantasia || empresa.razao_social)}</div>
      <div>${[empresa.filial && `Filial ${esc(empresa.filial)}`, empresa.cnpj && `CNPJ ${esc(empresa.cnpj)}`].filter(Boolean).join(' · ')}</div>
    </div>
    <div class="direita">
      <h1>${ehCirurgia ? 'SEPARAÇÃO DE MATERIAL — CIRURGIA' : 'SEPARAÇÃO DE MATERIAL'}</h1>
      <div class="numero">${
        pedido?.pedido_erp
          ? `Pedido ${esc(pedido.pedido_erp)}`
          : `Cód. App ${esc(String(pedido?.pedido_id || '').substring(0, 8))}`
      }</div>
      <div>Emissão: ${esc([emissao.data, horaEmissao].filter(Boolean).join(' '))}</div>
    </div>
  </header>

  ${avisoErro}
  ${secaoCirurgia}

  <section>
    <h2>Hospital / Cliente</h2>
    <div class="grade quatro">
      ${campo('Nome', cirurgia?.hospital || cliente.fantasia)}
      <div class="campo duplo"><span class="rotulo">Razão social</span><span class="valor">${
        esc(cliente.razao_social) || '—'
      }</span></div>
      ${campo('CNPJ', cliente.cnpj)}
      <div class="campo duplo"><span class="rotulo">Endereço</span><span class="valor">${
        esc(enderecoCompleto(cliente.endereco)) || '—'
      }</span></div>
      ${campo('Telefone', cliente.telefone)}
      ${campo('Código / Loja', [cliente.codigo, cliente.loja].filter(Boolean).join(' / '))}
      ${cliente.endereco?.ponto_referencia ? `<div class="campo largo"><span class="rotulo">Ponto de referência</span><span class="valor">${esc(cliente.endereco.ponto_referencia)}</span></div>` : ''}
    </div>
  </section>

  <section>
    <h2>Pedido</h2>
    <div class="grade quatro">
      ${campo('Vendedor(a)', pedido?.colaboradores?.nome)}
      ${feitoPor ? campo('Feito por', feitoPor) : ''}
      ${aprovadoPor ? campo('Aprovado por', aprovadoPor) : ''}
      ${campo('Forma de pagamento', pedido?.forma_pagamento)}
      ${campo('Condição de pagamento', pedido?.condicao_pagamento)}
      ${String(pedido?.nota_erp ?? '').trim() ? campo('Nota fiscal', pedido.nota_erp) : ''}
      ${campo('Filial do pedido', pedido?.filial_erp)}
    </div>
  </section>

  <section>
    <h2>Materiais (${itens.length} ${itens.length === 1 ? 'item' : 'itens'} · ${totalPecas} ${totalPecas === 1 ? 'peça' : 'peças'})</h2>
    <table>
      <thead>
        <tr>
          <th class="centro">#</th>
          <th>Código</th>
          <th>Descrição</th>
          <th class="centro">Un.</th>
          <th class="centro">Qtd</th>
          <th class="direita">Valor unit.</th>
          <th class="direita">Total</th>
          <th class="centro">Separado</th>
        </tr>
      </thead>
      <tbody>${linhas || '<tr><td colspan="8" class="centro">Nenhum material neste pedido.</td></tr>'}</tbody>
      <tfoot>
        <tr><td colspan="6" class="direita">Total dos materiais</td><td class="direita">${moeda(totalItens)}</td><td></td></tr>
        ${frete ? `<tr><td colspan="6" class="direita">Frete</td><td class="direita">${moeda(frete)}</td><td></td></tr>` : ''}
        <tr><td colspan="6" class="direita">Total do pedido</td><td class="direita">${moeda(totalItens + frete)}</td><td></td></tr>
      </tfoot>
    </table>
  </section>

  <div class="assinaturas">
    <div>Separado por</div>
    <div>Conferido por</div>
    <div>Data / hora</div>
  </div>

  <footer>Impresso em ${esc(`${impressao.data} ${impressao.hora}`)}${impressoPor ? ` por ${esc(impressoPor)}` : ''}</footer>
</body>
</html>`;
}

/**
 * Imprime num iframe escondido — sem abrir aba (que o bloqueador de pop-up
 * pode barrar). O título da página vira o nome sugerido do arquivo ao
 * "Salvar como PDF", então ele é trocado durante a impressão e volta depois.
 */
export function imprimirHtml(html: string, titulo: string): void {
  document.getElementById('impressao-pedido')?.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'impressao-pedido';
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });
  document.body.appendChild(iframe);

  const janela = iframe.contentWindow;
  const doc = janela?.document;
  if (!janela || !doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const tituloOriginal = document.title;
  const restaurar = () => {
    document.title = tituloOriginal;
    // O iframe fica até a próxima impressão: removê-lo aqui cortaria a
    // impressão nos navegadores em que print() não bloqueia.
  };
  janela.addEventListener('afterprint', restaurar, { once: true });
  // Se o navegador não disparar afterprint, a aba não fica com o título trocado.
  setTimeout(restaurar, 60000);

  // Um respiro para o layout do iframe antes de abrir a janela de impressão.
  setTimeout(() => {
    document.title = titulo;
    janela.focus();
    janela.print();
  }, 100);
}
